import {
  ChangeEventHandler,
  FC,
  FocusEventHandler,
  InputHTMLAttributes,
  LegacyRef,
  ReactElement,
  ReactText,
  SyntheticEvent,
  useState,
} from 'react';
import cn from 'classnames';

import { InputModeTextAreaType } from 'core/types';
import { ErrorMessage } from '../../common/ErrorMessage';

export interface ITextArea
  extends Omit<Partial<InputHTMLAttributes<HTMLTextAreaElement>>, 'onChange'> {
  name: string;
  parsers?: Array<(value: string, name: string) => string>;
  onChange?: (name: string, value: any, e: SyntheticEvent<any>) => void;
  label?: string | ReactElement;
  inputMode?: InputModeTextAreaType;
  error?: string;
  inputRef?: LegacyRef<ReactText> | undefined;
  classNames?: {
    root?: string;
    label?: string;
    input?: string;
    error?: string;
  };
}

export const TextArea: FC<ITextArea> = (props) => {
  const {
    onChange,
    parsers,
    onBlur,
    onFocus,
    label,
    name,
    error,
    classNames,
    inputRef,
    value,
    children,
    ...rest
  } = props;

  const [isFocus, setIsFocus] = useState<boolean>(false);

  const isEmpty = !!`${props.value || ''}`.length || isFocus;

  const handleBlur: FocusEventHandler<HTMLTextAreaElement> = (e) => {
    onBlur && onBlur(e);
    setIsFocus(false);
  };

  const handleFocus: FocusEventHandler<HTMLTextAreaElement> = (e) => {
    onFocus && onFocus(e);
    setIsFocus(true);
  };

  const handleChange: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    const val = parsers
      ? parsers.reduce(
          (acc: string, cur: (v: string, name: string) => string): string => cur(acc, name),
          e.target.value
        )
      : e.target.value;
    onChange && onChange(name, val, e);
  };

  return (
    <div
      className={cn(
        'text-area',
        { 'text-area__invalid': !!error },
        { 'text-area__active': isFocus },
        { 'text-area__no-empty': isEmpty },
        { 'text-area__disabled': props.disabled },
        classNames?.root
      )}
    >
      {children}
      {label && (
        <label className={cn('text-area__label', classNames?.label)} htmlFor={name}>
          {label}
        </label>
      )}
      <textarea
        name={name}
        className={cn(classNames?.input)}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onChange={handleChange}
        value={value || ''}
        {...rest}
      />

      {error && <ErrorMessage classError={classNames?.error} message={error} />}
    </div>
  );
};

TextArea.defaultProps = {
  inputMode: 'text',
};
